import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  FileText, Search, Filter, Printer, Eye, Download, 
  CheckCircle, Clock, Truck, Package, AlertCircle, X,
  Calendar, DollarSign, User, MapPin, Loader2, RefreshCw
} from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Orders = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [carregando, setCarregando] = useState(true);

  // ACHADO DA SESSÃO DE DEPLOY: esta tela sempre mostrava 6 pedidos de
  // exemplo (Carlos Mendes, Ana Paula etc.), nunca conectados a nada real
  // - qualquer admin que clicasse aqui via "Todos os Pedidos" via dados
  // inventados. Agora busca os pedidos reais no mesmo endpoint já usado
  // pela tela de Repasses, adaptando os nomes de campo.
  useEffect(() => {
    const carregarPedidos = async () => {
      try {
        const resp = await authService.fetchAuth(`${BACKEND_URL}/api/business/admin/pedidos?limit=100`);
        if (!resp.ok) return;
        const dados = await resp.json();
        const mapeados = dados.map((p) => ({
          id: p.codigo || p.id,
          date: p.created_at ? new Date(p.created_at).toLocaleString('pt-BR') : '',
          product: p.produto_nome || 'Produto',
          productImage: null,
          quantity: p.quantidade || 1,
          price: (p.preco_venda || 0) / (p.quantidade || 1),
          total: p.preco_venda || 0,
          status: p.status || 'pendente',
          affiliate: p.afiliado_id || '',
          supplier: p.fornecedor_id || '',
          customer: {
            name: p.cliente_nome || '',
            email: p.cliente_email || '',
            phone: p.cliente_telefone || '',
            address: p.endereco_entrega || ''
          },
          tracking: p.marketplace_order_id || null,
          commission: p.lucro_afiliado || 0
        }));
        setOrders(mapeados);
      } catch (error) {
        console.error('Erro ao carregar pedidos:', error);
      } finally {
        setCarregando(false);
      }
    };
    carregarPedidos();
  }, []);


  const statusConfig = {
    pendente: { label: "Pendente", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
    pago: { label: "Pago", color: "bg-blue-500/20 text-blue-400", icon: DollarSign },
    enviado: { label: "Enviado", color: "bg-purple-500/20 text-purple-400", icon: Truck },
    entregue: { label: "Entregue", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
    cancelado: { label: "Cancelado", color: "bg-red-500/20 text-red-400", icon: AlertCircle }
  };

  const filteredOrders = orders.filter(order => {
    const matchSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        order.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        order.customer.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === "all" || order.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const solicitarEstorno = async (order) => {
    const motivo = window.prompt("Descreva o motivo da solicitação de estorno:");
    if (!motivo || !motivo.trim()) {
      return;
    }
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/estornos/solicitar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pedido_id: order.id, motivo: motivo.trim() }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao solicitar estorno");
      alert("Solicitação de estorno enviada! O administrador vai analisar.");
      setSelectedOrder(null);
    } catch (error) {
      alert(error.message);
    }
  };

  const handlePrint = (order) => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pedido ${order.id}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .header h1 { color: #f97316; margin-bottom: 5px; }
          .section { margin-bottom: 20px; }
          .section h3 { border-bottom: 2px solid #f97316; padding-bottom: 5px; margin-bottom: 10px; }
          .row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .label { color: #666; }
          .total { font-size: 24px; color: #f97316; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>ClickHub</h1>
          <p>Comprovante de Pedido</p>
        </div>
        
        <div class="section">
          <h3>Informações do Pedido</h3>
          <div class="row"><span class="label">Número:</span> <span>${order.id}</span></div>
          <div class="row"><span class="label">Data:</span> <span>${order.date}</span></div>
          <div class="row"><span class="label">Status:</span> <span>${statusConfig[order.status].label}</span></div>
        </div>
        
        <div class="section">
          <h3>Produto</h3>
          <div class="row"><span class="label">Nome:</span> <span>${order.product}</span></div>
          <div class="row"><span class="label">Quantidade:</span> <span>${order.quantity}</span></div>
          <div class="row"><span class="label">Preço Unit.:</span> <span>R$ ${order.price.toFixed(2)}</span></div>
        </div>
        
        <div class="section">
          <h3>Cliente</h3>
          <div class="row"><span class="label">Nome:</span> <span>${order.customer.name}</span></div>
          <div class="row"><span class="label">Email:</span> <span>${order.customer.email}</span></div>
          <div class="row"><span class="label">Telefone:</span> <span>${order.customer.phone}</span></div>
          <div class="row"><span class="label">Endereço:</span> <span>${order.customer.address}</span></div>
        </div>
        
        <div class="section">
          <h3>Valores</h3>
          <div class="row"><span class="label">Total:</span> <span class="total">R$ ${order.total.toFixed(2)}</span></div>
          ${order.tracking ? `<div class="row"><span class="label">Rastreio:</span> <span>${order.tracking}</span></div>` : ''}
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const totalValue = filteredOrders.reduce((sum, o) => sum + o.total, 0);
  const totalCommission = filteredOrders.reduce((sum, o) => sum + o.commission, 0);

  return (
    <div className="flex flex-col h-full bg-gray-950">
      {/* Header Fixo */}
      <div className="p-4 lg:p-6 border-b border-gray-800">
        <div className="flex items-center gap-2 mb-1">
          <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-2 py-1 rounded-full uppercase">Pedidos</span>
          <h1 className="text-xl font-bold text-white">Gestão de Pedidos</h1>
        </div>
        <p className="text-sm text-gray-400">Visualize, gerencie e imprima todos os seus pedidos</p>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 border-none">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs mb-1">Total Pedidos</p>
                <p className="text-white text-2xl font-bold">{filteredOrders.length}</p>
              </div>
              <Package className="w-6 h-6 text-white/80" />
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 p-3 border-none">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs mb-1">Valor Total</p>
                <p className="text-white text-xl font-bold">R$ {totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              </div>
              <DollarSign className="w-6 h-6 text-white/80" />
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 border-none">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs mb-1">Comissões</p>
                <p className="text-white text-xl font-bold">R$ {totalCommission.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              </div>
              <CheckCircle className="w-6 h-6 text-white/80" />
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 border-none">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs mb-1">Entregues</p>
                <p className="text-white text-2xl font-bold">{orders.filter(o => o.status === 'entregue').length}</p>
              </div>
              <Truck className="w-6 h-6 text-white/80" />
            </div>
          </Card>
        </div>

        {/* Filtros */}
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar por ID, produto ou cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-gray-800 border-gray-700 h-9 pl-9 text-sm"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["all", "pendente", "entregue", "cancelado"].map((status) => (
              <Button
                key={status}
                size="sm"
                onClick={() => setFilterStatus(status)}
                className={`h-9 text-xs ${filterStatus === status 
                  ? 'bg-orange-500 hover:bg-orange-600' 
                  : 'bg-gray-700 hover:bg-gray-600'}`}
              >
                {status === 'all' ? 'Todos' : statusConfig[status]?.label || status}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Lista de Pedidos com Scroll */}
      <div className="flex-1 p-4 lg:p-6">
        {carregando && (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Carregando pedidos...
          </div>
        )}
        <div className="space-y-3 pb-4">
          {!carregando && filteredOrders.map((order) => {
            const StatusIcon = statusConfig[order.status]?.icon || Package;
            return (
              <Card 
                key={order.id} 
                className="bg-gray-800/50 border-gray-700 p-4 hover:border-gray-600 transition-all"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Imagem e Info Principal */}
                  <div className="flex items-center gap-4 flex-1">
                    <img 
                      src={order.productImage} 
                      alt={order.product}
                      className="w-16 h-16 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.src = "/images/clickhub-logo.png";
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-bold">{order.id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 ${statusConfig[order.status]?.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig[order.status]?.label}
                        </span>
                      </div>
                      <p className="text-white text-sm font-medium truncate">{order.product}</p>
                      <p className="text-gray-400 text-xs">Qtd: {order.quantity} • {order.date}</p>
                    </div>
                  </div>

                  {/* Cliente */}
                  <div className="lg:w-48">
                    <p className="text-xs text-gray-400 mb-0.5">Cliente</p>
                    <p className="text-white text-sm font-medium truncate">{order.customer.name}</p>
                    <p className="text-gray-400 text-xs truncate">{order.customer.phone}</p>
                  </div>

                  {/* Valor */}
                  <div className="lg:w-32 lg:text-right">
                    <p className="text-xs text-gray-400 mb-0.5">Total</p>
                    <p className="text-green-400 text-lg font-bold">R$ {order.total.toFixed(2)}</p>
                    <p className="text-gray-400 text-xs">Comissão: R$ {order.commission.toFixed(2)}</p>
                  </div>

                  {/* Ações */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-gray-700 h-8"
                      onClick={() => setSelectedOrder(order)}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Ver
                    </Button>
                    <Button
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-600 h-8"
                      onClick={() => handlePrint(order)}
                    >
                      <Printer className="w-3 h-3 mr-1" />
                      Imprimir
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}

          {filteredOrders.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <h3 className="text-lg font-semibold text-white mb-2">Nenhum pedido encontrado</h3>
              <p className="text-gray-400">Tente ajustar seus filtros ou termo de busca</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Detalhes do Pedido */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="bg-gray-900 border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-white">Pedido {selectedOrder.id}</h2>
                  <p className="text-gray-400 text-sm">{selectedOrder.date}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedOrder(null)}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Status */}
              <div className="mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusConfig[selectedOrder.status]?.color}`}>
                  {statusConfig[selectedOrder.status]?.label}
                </span>
              </div>

              {/* Produto */}
              <div className="mb-4 p-4 bg-gray-800/50 rounded-lg">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4 text-orange-400" />
                  Produto
                </h3>
                <div className="flex items-center gap-4">
                  <img 
                    src={selectedOrder.productImage} 
                    alt={selectedOrder.product}
                    className="w-20 h-20 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = "/images/clickhub-logo.png";
                    }}
                  />
                  <div>
                    <p className="text-white font-medium">{selectedOrder.product}</p>
                    <p className="text-gray-400 text-sm">Quantidade: {selectedOrder.quantity}</p>
                    <p className="text-gray-400 text-sm">Preço Unit.: R$ {selectedOrder.price.toFixed(2)}</p>
                    <p className="text-green-400 font-bold">Total: R$ {selectedOrder.total.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Cliente */}
              <div className="mb-4 p-4 bg-gray-800/50 rounded-lg">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-400" />
                  Cliente
                </h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-gray-400">Nome:</span> <span className="text-white">{selectedOrder.customer.name}</span></p>
                  <p><span className="text-gray-400">Email:</span> <span className="text-white">{selectedOrder.customer.email}</span></p>
                  <p><span className="text-gray-400">Telefone:</span> <span className="text-white">{selectedOrder.customer.phone}</span></p>
                  <p className="flex items-start gap-1">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span className="text-white">{selectedOrder.customer.address}</span>
                  </p>
                </div>
              </div>

              {/* Rastreio */}
              {selectedOrder.tracking && (
                <div className="mb-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-purple-400" />
                    Rastreamento
                  </h3>
                  <p className="text-purple-300 font-mono">{selectedOrder.tracking}</p>
                </div>
              )}

              {/* Envolvidos */}
              <div className="mb-4 p-4 bg-gray-800/50 rounded-lg">
                <h3 className="text-white font-semibold mb-2">Envolvidos na Venda</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">Afiliado:</p>
                    <p className="text-white">{selectedOrder.affiliate}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Fornecedor:</p>
                    <p className="text-white">{selectedOrder.supplier}</p>
                  </div>
                </div>
              </div>

              {/* Comissão */}
              <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Comissão do Afiliado:</span>
                  <span className="text-green-400 text-xl font-bold">R$ {selectedOrder.commission.toFixed(2)}</span>
                </div>
              </div>

              {/* Ações */}
              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={() => handlePrint(selectedOrder)}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir Pedido
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-gray-700"
                  onClick={() => setSelectedOrder(null)}
                >
                  Fechar
                </Button>
              </div>

              {/* Solicitar estorno - só faz sentido para pedidos já
                  entregues (achado da varredura: o sistema mencionava
                  "abra um processo de estorno" mas esse processo não
                  existia de verdade) */}
              {selectedOrder.status === 'entregue' && (
                <Button
                  variant="outline"
                  className="w-full mt-3 border-red-800 text-red-400 hover:bg-red-950/30"
                  onClick={() => solicitarEstorno(selectedOrder)}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Solicitar Estorno
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Orders;
